import { B4_MARKER_FIELDS } from './b4Markers';
import { PRINT_DEFAULTS, loadPrintPrefs, normalizePrintPrefs } from './printPrefs';

const PREFIX = 'pv4-print-draft:v1:';
const KINDS = new Set(['paper', 'preset', 'exam', 'assignment', 'note', 'draft']);
const sessionDrafts = new Map();
let draftSequence = 0;

export const B4_HEADER_FIELDS = ['b4Line1', 'b4Title', 'b4Subject', 'b4Line2', 'b4Cover', 'b4CoverPhrase'];
const TEXT_FIELDS = [...B4_HEADER_FIELDS, ...B4_MARKER_FIELDS.map(({ labelKey }) => labelKey)];

export function normalizePrintContext(context) {
  if (!KINDS.has(context?.kind) || !['string', 'number'].includes(typeof context?.id)) return null;
  const id = String(context.id).trim();
  return id ? { kind: context.kind, id } : null;
}

export function createPrintDraftContext() {
  draftSequence += 1;
  const id = window.crypto?.randomUUID?.() || `${Date.now()}-${draftSequence}`;
  return { kind: 'draft', id };
}

export function printContextOf(testData) {
  return normalizePrintContext(testData?.printContext)
    || normalizePrintContext({ kind: 'paper', id: testData?.paper_id })
    || normalizePrintContext({ kind: 'preset', id: testData?.preset_id })
    || normalizePrintContext({ kind: 'exam', id: testData?.exam_id });
}

export function printDraftScope(userId, context) {
  const normalized = normalizePrintContext(context);
  return normalized ? JSON.stringify([userId == null ? null : String(userId), normalized.kind, normalized.id]) : null;
}

const persists = (userId, context) => userId != null && String(userId).trim() !== '' && context?.kind !== 'draft';
const problemIdOf = (id) => id == null || String(id).trim() === '' ? null : String(id);

// 버전·필드 검증. 문제 번호는 저장하지 않고 대상 problem_id만 보관한다.
export function normalizePrintDraft(raw) {
  if (!raw || raw.version !== 1 || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const prefs = normalizePrintPrefs({ ...raw, patPreset: { B4: raw.b4Preset } });
  const out = { version: 1, updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
    b4Preset: prefs.patPreset.B4, markerProblemIds: {} };
  TEXT_FIELDS.forEach((field) => { out[field] = prefs[field]; });
  B4_MARKER_FIELDS.forEach(({ numberKey }) => {
    const value = raw.markerProblemIds?.[numberKey];
    out.markerProblemIds[numberKey] = typeof value === 'string' || typeof value === 'number' ? problemIdOf(value) : null;
  });
  return out;
}

export function printDraftFromPrefs(prefs, problemIds) {
  const markerProblemIds = {};
  B4_MARKER_FIELDS.forEach(({ numberKey }) => {
    const number = Number(prefs[numberKey]);
    markerProblemIds[numberKey] = Number.isInteger(number) && number > 0
      ? problemIdOf(problemIds[number - 1]) : null;
  });
  return normalizePrintDraft({ ...prefs, version: 1, updatedAt: new Date().toISOString(),
    b4Preset: prefs.patPreset.B4, markerProblemIds });
}

export function prefsFromPrintDraft(draft, problemIds, sharedPrefs = loadPrintPrefs()) {
  // 새 시험지에 이전 공통 B4 문구를 복사하지 않는다. 공통 용지·배율·A4 패턴만 유지한다.
  const b4 = { ...PRINT_DEFAULTS, ...(draft || {}), patPreset: { B4: draft?.b4Preset || PRINT_DEFAULTS.patPreset.B4 } };
  if (draft) {
    B4_MARKER_FIELDS.forEach(({ numberKey }) => {
      const target = draft.markerProblemIds[numberKey];
      const index = target == null ? -1 : problemIds.findIndex((id) => problemIdOf(id) === target);
      b4[numberKey] = index < 0 ? 'none' : String(index + 1);
    });
  }
  const validated = normalizePrintPrefs(b4);
  return { ...sharedPrefs, ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, validated[field]])),
    ...Object.fromEntries(B4_MARKER_FIELDS.map(({ numberKey }) => [numberKey, validated[numberKey]])),
    patPreset: { ...sharedPrefs.patPreset, B4: validated.patPreset.B4 } };
}

export function readPrintDraft(userId, context) {
  const scope = printDraftScope(userId, context);
  if (!scope) return { draft: null, status: 'new' };
  const memory = sessionDrafts.get(scope);
  if (memory) return memory;
  if (!persists(userId, context)) return { draft: null, status: 'session' };
  try {
    const raw = window.localStorage.getItem(PREFIX + scope);
    const draft = raw ? normalizePrintDraft(JSON.parse(raw)) : null;
    return { draft, status: draft ? 'saved' : 'new' };
  } catch (error) {
    return { draft: null, status: 'unavailable' };
  }
}

export function writePrintDraft(userId, context, draft) {
  const scope = printDraftScope(userId, context);
  if (!scope) return 'session';
  if (!persists(userId, context)) {
    sessionDrafts.set(scope, { draft, status: 'session' });
    return 'session';
  }
  try {
    window.localStorage.setItem(PREFIX + scope, JSON.stringify(draft));
    sessionDrafts.delete(scope);
    return 'saved';
  } catch (error) {
    sessionDrafts.set(scope, { draft, status: 'unavailable' });
    return 'unavailable';
  }
}

// 서버 생성이 성공한 시점에 호출한다. 호출자가 바로 닫혀도 초안 설정을 잃지 않는다.
export function promotePrintDraft(userId, fromContext, paperId) {
  const { draft } = readPrintDraft(userId, fromContext);
  if (!draft || paperId == null) return;
  writePrintDraft(userId, { kind: 'paper', id: String(paperId) }, draft);
  sessionDrafts.delete(printDraftScope(userId, fromContext));
}

export function loadScopedPrintPrefs(userId, context, problemIds) {
  const shared = loadPrintPrefs();
  return context ? prefsFromPrintDraft(readPrintDraft(userId, context).draft, problemIds, shared) : shared;
}
